import * as React from "react";
import { Organisation, OrganisationLineItem } from "@interfaces/organisation";
import { decodeOrganisation, encodeOrganisation } from "decoders/organisation";
import { getApiData, listApiData, postApiData } from "@hooks/utils/api";

export const useOrganisation = () => {
  const [projectOrganisation, setProjectOrganisation] =
    React.useState<OrganisationLineItem | null>(null);
  const [userOrganisation, setUserOrganisation] =
    React.useState<OrganisationLineItem | null>(null);

  const [organisations, setOrganisations] = React.useState<
    OrganisationLineItem[]
  >([]);

  const [isOrganisationLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const getOrganisation = async (
    id: string
  ): Promise<OrganisationLineItem | null> => {
    try {
      setIsLoading(true);
      const organisationResult = await getApiData(
        "getOrganisation",
        "organisation",
        id
      );
      if (Object.keys(organisationResult.data).length === 0) {
        return null;
      }

      const organisationLineItem = organisationResult
        ? await decodeOrganisation(organisationResult.data as Organisation)
        : null;

      return organisationLineItem;
    } catch (e: any) {
      setError("Could not fetch organisation");
    } finally {
      setIsLoading(false);
    }
    return null;
  };

  const getProjectOrganisation = async (id: string): Promise<void> => {
    try {
      setIsLoading(true);
      const organisationResult = await getApiData(
        "getProjectOrganisation",
        "organisation",
        id
      );
      if (Object.keys(organisationResult.data).length === 0) {
        return undefined;
      }

      const organisationLineItem = organisationResult
        ? await decodeOrganisation(organisationResult.data as Organisation)
        : null;
      setProjectOrganisation(organisationLineItem);
    } catch (e: any) {
      setError("Could not fetch organisation");
    } finally {
      setIsLoading(false);
    }
  };

  const getProfileOrganisation = async (id: string): Promise<void> => {
    try {
      setIsLoading(true);
      const organisationResult = await getApiData(
        "getOrganisation",
        "organisation",
        id
      );
      if (Object.keys(organisationResult.data).length === 0) {
        return undefined;
      }
      const organisationLineItem = organisationResult
        ? await decodeOrganisation(organisationResult.data as Organisation)
        : null;
      setUserOrganisation(organisationLineItem);
    } catch (e: any) {
      setError("Could not fetch organisation");
    } finally {
      setIsLoading(false);
    }
  };

  const listOrganisations = async (organisationId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const organisationResult = await listApiData(
        "listOrganisations",
        "organisation",
        organisationId
      );

      const organisationLineItems = await Promise.all(
        organisationResult.data.map((p) =>
          decodeOrganisation(p as Organisation)
        )
      );
      setOrganisations(organisationLineItems);
    } catch (e: any) {
      setError("Could not list organisations");
    } finally {
      setIsLoading(false);
    }
  };

  const searchOrganisations = async (
    query: string
  ): Promise<OrganisationLineItem[]> => {
    try {
      const organisationResult = await listApiData(
        "searchOrganisations",
        "organisation",
        query
      );

      return await Promise.all(
        organisationResult.data.map((p) =>
          decodeOrganisation(p as Organisation)
        )
      );
    } catch (e: any) {
      setError("Could not search organisations");
      return [];
    }
  };

  const createOrganisation = async (
    organisation: OrganisationLineItem
  ): Promise<OrganisationLineItem> => {
    try {
      setIsLoading(true);
      const encodedOrganisation = encodeOrganisation(organisation);
      const upsertedOrganisation = await postApiData(
        "createOrganisation",
        "organisation",
        encodedOrganisation
      );
      const decodedOrganisation = await decodeOrganisation(
        upsertedOrganisation.data as Organisation
      );
      setOrganisations([...organisations, decodedOrganisation]);
      return decodedOrganisation;
    } catch (e: any) {
      setError("Could not list organisations");
    } finally {
      setIsLoading(false);
    }
    return organisation;
  };

  const updateOrganisation = async (
    organisation: OrganisationLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedOrganisation = encodeOrganisation(organisation);
      await postApiData(
        "updateOrganisation",
        "organisation",
        encodedOrganisation
      );
      setOrganisations(
        organisations.map((c) => {
          if (c.id === organisation.id) {
            return organisation;
          }
          return c;
        })
      );
    } catch (e: any) {
      setError("Could not update organisation");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteOrganisation = async (
    organisation: OrganisationLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedOrganisation = encodeOrganisation(organisation);
      await postApiData(
        "deleteOrganisation",
        "organisation",
        encodedOrganisation
      );
      setOrganisations(organisations.filter((p) => p.id !== organisation.id));
    } catch (e: any) {
      setError("Could not delete organisation");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    organisations,
    userOrganisation,
    projectOrganisation,
    getOrganisation,
    getProfileOrganisation,
    getProjectOrganisation,
    listOrganisations,
    createOrganisation,
    updateOrganisation,
    deleteOrganisation,
    searchOrganisations,
    isOrganisationLoading,
    error,
  };
};
